import {
  type FC,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
  type UIEvent,
  useId,
} from "react";

import { MagnifyingGlass, X } from "@phosphor-icons/react";
import {
  Badge,
  Box,
  Flex,
  IconButton,
  Popover,
  Tabs,
  Text,
  TextField,
} from "@radix-ui/themes";
// Radix Themes' `Popover.Anchor` drops its children; the underlying primitive
// from `radix-ui` shares the same context, so we import Anchor directly.
import { Popover as PopoverPrimitive } from "radix-ui";

import { cn } from "~/v2/utils/cn";
import type {
  AssetSearchInputProps,
  AssetSearchTab,
  EnrichedAsset,
} from "./AssetSearch.types";
import { AssetQuotePresentation } from "./assetQuote/AssetQuote.pres";
import type { AssetSearchTabId } from "./useAssetSearchQueries";

export interface AssetSearchControllerPresentationValue
  extends AssetSearchInputProps {
  query: string;
  onQueryChange: (value: string) => void;
  onFocus: () => void;
  onClose: () => void;
  isOpen: boolean; // effective open state only
  assets: EnrichedAsset[];
  tabs: readonly AssetSearchTab[];
  activeTab: AssetSearchTabId;
  onTabChange: (id: AssetSearchTabId) => void;
  highlightedIndex: number;
  listRef: RefObject<HTMLDivElement>;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onScroll: (event: UIEvent<HTMLDivElement>) => void;
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onSelect: (asset: EnrichedAsset) => void;
  onClear?: () => void;
  placeholder: string;
  autofocus?: boolean;
  showFilters: boolean;
  dropdownRef: RefObject<HTMLDivElement>;
}

export interface AssetSearchPresentationProps
  extends AssetSearchControllerPresentationValue {
  assetIcons: Readonly<Record<string, ReactNode>>;
}

/** Stable string keys for skeleton placeholder rows — avoids index-as-key. */
const SKELETON_KEYS = ["sk-0", "sk-1", "sk-2", "sk-3", "sk-4"] as const;
const SKELETON_MORE_KEYS = ["sk-more-0", "sk-more-1"] as const;

/** Vertical offset between the search anchor and the dropdown surface. */
export const ASSET_SEARCH_DROPDOWN_GAP_PX = 8;

/**
 * Radix owns portal placement (rewrapped in `.radix-themes` so overlays inherit
 * appearance), positioning and width matching against the anchor, and
 * outside-click/Escape — which is why none of that appears here.
 *
 * The controller owns the outside-mousedown check, which also inspects
 * `dropdownRef` so a click inside the portaled popover does not close it.
 */
export const AssetSearchPresentation: FC<AssetSearchPresentationProps> = ({
  query,
  onQueryChange,
  onFocus,
  onClose,
  isOpen,
  assets,
  tabs,
  activeTab,
  onTabChange,
  highlightedIndex,
  listRef,
  onKeyDown,
  onScroll,
  isLoading,
  isLoadingMore,
  hasMore,
  onSelect,
  onClear,
  placeholder,
  autofocus,
  showFilters = true,
  assetIcons,
  dropdownRef,
  id,
  size,
  className,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  required,
}) => {
  const inputId = useId();
  const listboxId = useId();

  const activeDescendant =
    highlightedIndex >= 0
      ? `${listboxId}-option-${highlightedIndex}`
      : undefined;

  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      <PopoverPrimitive.Anchor data-testid="asset-search-anchor">
        <TextField.Root
          id={id ?? inputId}
          size={size}
          className={className}
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeDescendant}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-describedby={ariaDescribedby}
          aria-invalid={ariaInvalid}
          required={required}
          value={query}
          autoFocus={autofocus}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
        >
          <TextField.Slot>
            <MagnifyingGlass size={14} aria-hidden />
          </TextField.Slot>
          {query && onClear ? (
            <TextField.Slot side="right">
              <IconButton
                type="button"
                size="1"
                variant="ghost"
                color="gray"
                aria-label="Clear symbol search"
                onClick={onClear}
              >
                <X size={14} aria-hidden />
              </IconButton>
            </TextField.Slot>
          ) : null}
        </TextField.Root>
      </PopoverPrimitive.Anchor>

      <Popover.Content
        ref={dropdownRef}
        data-testid="asset-search-dropdown"
        align="start"
        sideOffset={ASSET_SEARCH_DROPDOWN_GAP_PX}
        // Radix keeps focus on the last-focused element (the input) when the
        // popover opens/closes, so search-as-you-type stays uninterrupted.
        onOpenAutoFocus={(e) => e.preventDefault()}
        onCloseAutoFocus={(e) => e.preventDefault()}
        className={cn(
          // Radix stamps --radix-popover-trigger-width on the content whenever
          // an anchor is present; pin width to it without measuring rects.
          "flex w-[var(--radix-popover-trigger-width)] flex-col overflow-hidden p-0",
          showFilters ? "max-h-[390px]" : "max-h-[346px]",
        )}
      >
        {showFilters && (
          <Box px="2" pt="3" flexShrink="0">
            <Tabs.Root
              value={activeTab}
              onValueChange={(value) => {
                const tab = tabs.find((candidate) => candidate.id === value);
                if (tab) {
                  onTabChange(tab.id);
                }
              }}
            >
              <Tabs.List aria-label="Asset filter tabs">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <Tabs.Trigger key={tab.id} value={tab.id}>
                      <Text size="1" weight="medium">
                        {tab.label}
                      </Text>
                      {tab.count >= 0 && (
                        <Badge
                          size="1"
                          variant="soft"
                          color={isActive ? "yellow" : "gray"}
                          className="atd-numeric"
                        >
                          {tab.count}
                        </Badge>
                      )}
                    </Tabs.Trigger>
                  );
                })}
              </Tabs.List>
            </Tabs.Root>
          </Box>
        )}

        <Box
          id={listboxId}
          ref={listRef}
          role="listbox"
          aria-label="Search results"
          onScroll={onScroll}
          flexGrow="1"
          className="min-h-0 overflow-y-auto scroll-smooth p-[var(--space-1)]"
        >
          {isLoading && assets.length === 0 ? (
            <Flex
              direction="column"
              gap="1"
              className={cn(showFilters && "mt-[var(--space-2)]")}
            >
              {SKELETON_KEYS.map((key) => (
                <AssetQuotePresentation
                  key={key}
                  isLoading={true}
                  isCompact={!showFilters}
                />
              ))}
            </Flex>
          ) : (
            <Flex
              direction="column"
              gap="1"
              className={cn(showFilters && "mt-[var(--space-2)]")}
            >
              {assets.map((asset, index) => {
                const optionId = `${listboxId}-option-${index}`;
                return (
                  <div
                    key={asset.id}
                    id={optionId}
                    role="option"
                    aria-selected={highlightedIndex === index}
                    tabIndex={-1}
                    data-asset-item
                  >
                    <AssetQuotePresentation
                      as="button"
                      onClick={() => onSelect(asset)}
                      symbol={asset.symbol}
                      companyName={asset.name}
                      price={asset.price}
                      dailyChange={asset.dailyChange}
                      dailyChangePercent={asset.dailyChangePercent}
                      icon={assetIcons[asset.symbol]}
                      showNotTradableIcon={!asset.tradable}
                      showOvernightHaltedIcon={asset.showOvernightHaltedIcon}
                      showNoOvernightSupportIcon={
                        asset.showNoOvernightSupportIcon
                      }
                      isLoading={isLoading}
                      isQuoteLoading={asset.isQuoteLoading}
                      isHighlighted={highlightedIndex === index}
                      isCompact={!showFilters}
                    />
                  </div>
                );
              })}

              {isLoadingMore && (
                <Flex direction="column" gap="1">
                  {SKELETON_MORE_KEYS.map((key) => (
                    <AssetQuotePresentation key={key} isLoading={true} />
                  ))}
                </Flex>
              )}

              {!isLoading && assets.length === 0 && (
                <Flex
                  direction="column"
                  align="center"
                  justify="center"
                  gap="4"
                  className="h-[340px]"
                >
                  <Text as="p" size="4" color="gray" m="0" align="center">
                    Try another search
                  </Text>
                  <Text
                    as="p"
                    size="2"
                    color="gray"
                    m="0"
                    align="center"
                    className="max-w-[413px]"
                  >
                    No assets matched your entry. Check for typos or try a
                    different symbol.
                  </Text>
                </Flex>
              )}

              {!hasMore && assets.length > 0 && (
                <Box p="4" className="text-center">
                  <Text size="1" color="gray">
                    End of results
                  </Text>
                </Box>
              )}
            </Flex>
          )}
        </Box>
      </Popover.Content>
    </Popover.Root>
  );
};
